import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../store/Auth';
import ThemeOptions from '../components/ThemeOptions';
import Sessions from '../components/Sessions';
import { DesktopControls } from '../components/Notifications';
import { T, useText } from '../components/Text';
import { usePage } from '../hooks/useResource';
import Api, { readPhoto } from '../services/api';

const draftFor = (user) => ({
  name: user?.displayName || '',
  username: user?.username || '',
  password: '',
  passwordConfirm: '',
  currentPassword: '',
});

/**
 * Hesap ve Ayarlar Sayfası (/account.html, /settings.html)
 * Profil, şifre değişikliği, oturumlar, masaüstü bildirimleri, dil ve tema tercihlerini yönetir.
 */
export default function Account({ settingsOnly = false }) {
  const { user } = useAuth();
  const { t } = useText();
  usePage(t('Hesap Ayarları'), 'admin-page settings-page');

  const [loaded, setLoaded] = useState(user);
  const [draft, setDraft] = useState(() => draftFor(user));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [removed, setRemoved] = useState(false);
  const [preview, setPreview] = useState(null);
  const [languageFeedback, setLanguageFeedback] = useState('');
  const [languageBusy, setLanguageBusy] = useState(false);

  const confirmRef = useRef(null);

  useEffect(() => {
    if (user?.version !== loaded?.version && !dirty && !busy) {
      setLoaded(user);
      setDraft(draftFor(user));
      setAvatar(null);
      setRemoved(false);
    }
  }, [user, loaded?.version, dirty, busy]);

  useEffect(() => {
    if (!avatar) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(avatar);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [avatar]);

  useEffect(() => {
    confirmRef.current?.setCustomValidity(
      draft.password !== draft.passwordConfirm ? t('Yeni şifreler aynı olmalı.') : ''
    );
  }, [draft.password, draft.passwordConfirm, t]);

  useEffect(() => {
    Api.hasUnsavedChanges = () => dirty;
    const leave = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', leave);
    return () => {
      Api.hasUnsavedChanges = null;
      window.removeEventListener('beforeunload', leave);
    };
  }, [dirty]);

  const updateDraft = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
    setFeedback('');
    setError('');
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError('');
    setFeedback('Kaydediliyor…');

    try {
      const body = {
        version: loaded.version,
        name: draft.name,
        username: draft.username,
        password: draft.password,
        currentPassword: draft.currentPassword,
      };

      if (avatar) body.avatar = await readPhoto(avatar, 2);
      if (removed) body.removeAvatar = true;

      const result = await Api.mutate('/api/account', 'PATCH', body);
      Api.setUser(result.user);
      setLoaded(result.user);
      setDraft(draftFor(result.user));
      setDirty(false);
      setAvatar(null);
      setRemoved(false);
      setFeedback('Hesap bilgileriniz kaydedildi.');
    } catch (failure) {
      setError(failure.message || 'Bilgiler kaydedilemedi.');
      setFeedback('');
    } finally {
      setBusy(false);
    }
  };

  const handleLanguageChange = async (value) => {
    setLanguageBusy(true);
    setLanguageFeedback('Kaydediliyor…');
    try {
      const result = await Api.mutate('/api/account/language', 'PATCH', { language: value });
      Api.setUser(result.user);
      setLanguageFeedback('Dil tercihiniz hesabınıza kaydedildi.');
    } catch (failure) {
      setLanguageFeedback(failure.message);
    } finally {
      setLanguageBusy(false);
    }
  };

  const avatarUrl = removed ? null : preview || loaded?.avatarUrl;
  const initialLetter = loaded?.displayName?.[0]?.toLocaleUpperCase('tr-TR') || 'K';

  return (
    <main id="main" className="settings-main">
      <div className="eyebrow">
        <T>HESABINIZA ÖZEL</T>
      </div>
      <h1>
        <T>Hesap Ayarları</T>
      </h1>
      <p className="settings-intro">
        <T>Profiliniz, giriş bilgileriniz ve size uygun görünüm.</T>
      </p>

      {/* Profil ve Giriş Bilgileri Paneli */}
      <section className="account-panel" aria-labelledby="profile-title">
        <div className="account-panel-heading">
          <h2 id="profile-title">
            <T>Profil ve giriş bilgileri</T>
          </h2>
          <span className="profile-badge">
            <T>Size özel</T>
          </span>
        </div>

        {user?.version !== loaded?.version && (
          <p className="account-live-note" id="account-live-note">
            <T>
              Hesabınız başka bir ekranda güncellendi. Değişikliklerinizi kaydetmeden önce sayfayı
              yenileyin.
            </T>
          </p>
        )}

        <form id="account-form" onSubmit={handleSaveProfile}>
          <fieldset className="profile-fields" disabled={busy}>
            <legend className="visually-hidden">
              <T>Hesap bilgileri</T>
            </legend>

            {/* Avatar Editörü */}
            <div className="avatar-editor">
              <div className="profile-avatar">
                {avatarUrl ? (
                  <img id="avatar-preview" src={avatarUrl} alt={t('Profil fotoğrafınız')} />
                ) : (
                  <span id="avatar-initial" aria-hidden="true">
                    {initialLetter}
                  </span>
                )}
              </div>
              <div>
                <strong id="profile-name">{loaded?.displayName}</strong>
                <p>
                  <T>PNG, JPG veya WebP · En fazla 2 MB</T>
                </p>
                <div className="avatar-buttons">
                  <label className="avatar-upload">
                    <T>Fotoğraf seç</T>
                    <input
                      id="avatar-file"
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (
                          !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
                          !file.size ||
                          file.size > 2 * 1024 * 1024
                        ) {
                          setError('PNG, JPG veya WebP seçin. Profil fotoğrafı en fazla 2 MB olabilir.');
                          e.target.value = '';
                          return;
                        }
                        setAvatar(file);
                        setRemoved(false);
                        setDirty(true);
                      }}
                    />
                  </label>
                  {avatarUrl && (
                    <button
                      type="button"
                      id="remove-avatar"
                      onClick={() => {
                        setRemoved(true);
                        setAvatar(null);
                        setDirty(true);
                      }}
                    >
                      <T>Fotoğrafı kaldır</T>
                    </button>
                  )}
                </div>
                {(avatar || removed) && (
                  <span className="avatar-note" id="avatar-note" role="status">
                    <T>
                      {avatar
                        ? 'Fotoğrafı kaydetmek için Değişiklikleri Kaydet düğmesine basın.'
                        : 'Kaydettiğinizde profil fotoğrafınız kaldırılır.'}
                    </T>
                  </span>
                )}
              </div>
            </div>

            {/* İsim ve Kullanıcı Adı */}
            <div className="account-fields-grid">
              <label className="account-field">
                <T>İsim / Firma İsmi</T>
                <input
                  name="name"
                  required
                  maxLength={120}
                  autoComplete="organization"
                  placeholder={t('İsminiz veya firma isminiz')}
                  value={draft.name}
                  onChange={(e) => updateDraft('name', e.target.value)}
                />
              </label>

              <label className="account-field">
                <T>Kullanıcı Adı</T>
                <input
                  name="username"
                  required
                  minLength={3}
                  maxLength={80}
                  pattern="[A-Za-z0-9_.\-]+"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck="false"
                  value={draft.username}
                  onChange={(e) => updateDraft('username', e.target.value)}
                />
                <span>
                  <T>İngilizce harf, rakam, nokta, tire veya alt çizgi.</T>
                </span>
              </label>
            </div>

            {/* Şifre Değiştirme */}
            <div className="account-security">
              <h3>
                <T>Şifre değiştir</T>
              </h3>
              <p>
                <T>Şifrenizi değiştirmek istemiyorsanız yeni şifre alanlarını boş bırakın.</T>
              </p>

              <div className="account-fields-grid">
                <label className="account-field">
                  <T>Yeni Şifre</T>
                  <input
                    name="password"
                    type="password"
                    minLength={6}
                    maxLength={200}
                    autoComplete="new-password"
                    placeholder={t('En az 6 karakter')}
                    value={draft.password}
                    onChange={(e) => updateDraft('password', e.target.value)}
                  />
                </label>

                <label className="account-field">
                  <T>Yeni Şifre Tekrar</T>
                  <input
                    ref={confirmRef}
                    name="passwordConfirm"
                    type="password"
                    maxLength={200}
                    autoComplete="new-password"
                    placeholder={t('Yeni şifrenizi tekrar girin')}
                    required={Boolean(draft.password)}
                    value={draft.passwordConfirm}
                    onChange={(e) => updateDraft('passwordConfirm', e.target.value)}
                  />
                </label>
              </div>

              <label className="account-field current-password">
                <T>Mevcut Şifre</T>
                <input
                  name="currentPassword"
                  type="password"
                  maxLength={200}
                  autoComplete="current-password"
                  placeholder={t('Mevcut şifreniz')}
                  required={draft.username.trim() !== loaded?.username || Boolean(draft.password)}
                  value={draft.currentPassword}
                  onChange={(e) => updateDraft('currentPassword', e.target.value)}
                />
                <span>
                  <T>
                    Kullanıcı adı veya şifre değiştirirken gereklidir. Diğer cihazlardaki oturumlarınız
                    kapatılır.
                  </T>
                </span>
              </label>
            </div>

            {error && (
              <p className="form-error" id="account-error" role="alert">
                <T>{error}</T>
              </p>
            )}

            <div className="account-actions">
              {feedback && (
                <span id="account-feedback" role="status" aria-live="polite">
                  <T>{feedback}</T>
                </span>
              )}
              <button className="button gold" type="submit" id="save-account">
                <T>{busy ? 'Kaydediliyor…' : 'Değişiklikleri Kaydet ↗'}</T>
              </button>
            </div>
          </fieldset>
        </form>
      </section>

      {/* Aktif Oturumlar Paneli */}
      <section className="account-panel" aria-labelledby="sessions-title">
        <div className="account-panel-heading">
          <h2 id="sessions-title">
            <T>Giriş bilgileri</T>
          </h2>
          <span className="profile-badge">
            <T>Size özel</T>
          </span>
        </div>
        <div id="account-sessions">
          <Sessions />
        </div>
      </section>

      {/* Masaüstü Bildirimleri Paneli */}
      <section className="account-panel" aria-labelledby="desktop-title">
        <div className="account-panel-heading">
          <h2 id="desktop-title">
            <T>Masaüstü bildirimleri</T>
          </h2>
          <span className="profile-badge">
            <T>Bu tarayıcı</T>
          </span>
        </div>
        <p className="desktop-notification-description">
          <T>
            Site açıkken gelen bildirimleri Windows’un bildirim alanında da görün. Bu tercih yalnızca bu
            hesap ve tarayıcı için geçerlidir.
          </T>
        </p>
        <div id="account-desktop-notifications">
          <DesktopControls />
        </div>
      </section>

      {/* Dil Tercihi Paneli */}
      <section className="account-panel language-panel" aria-labelledby="language-title">
        <div className="account-panel-heading">
          <h2 id="language-title">
            <T>Dil tercihiniz</T>
          </h2>
          <span className="profile-badge">
            <T>Size özel</T>
          </span>
        </div>
        <p>
          <T>
            Panelinizi Türkçe, İngilizce veya Arapça kullanabilirsiniz. Dil tercihiniz yalnızca sizin
            hesabınıza kaydedilir.
          </T>
        </p>
        <label className="account-field language-field">
          <span>
            <T>Dil</T>
          </span>
          <select
            id="account-language"
            disabled={languageBusy}
            aria-label={t('Hesap dili')}
            value={user?.language || 'tr'}
            onChange={(e) => handleLanguageChange(e.target.value)}
          >
            <option translate="no" value="tr" lang="tr">
              Türkçe
            </option>
            <option translate="no" value="en" lang="en">
              English
            </option>
            <option translate="no" value="ar" lang="ar" dir="rtl">
              العربية
            </option>
          </select>
        </label>
        {languageFeedback && (
          <p id="language-feedback" role="status" aria-live="polite">
            <T>{languageFeedback}</T>
          </p>
        )}
      </section>

      {/* Tema Tercihi Paneli (8 Farklı Tema Seçeneği) */}
      <section className="appearance-panel" aria-labelledby="appearance-title">
        <div className="appearance-heading">
          <div>
            <h2 id="appearance-title">
              <T>Tema tercihiniz</T>
            </h2>
            <p>
              <T>Yalnızca sizin hesabınızın görünümünü değiştirir.</T>
            </p>
          </div>
          <span className="appearance-icon" aria-hidden="true">
            ◐
          </span>
        </div>
        <fieldset className="theme-options">
          <legend className="visually-hidden">
            <T>Panel teması</T>
          </legend>
          <ThemeOptions />
        </fieldset>
        <div className="theme-note">
          <p>
            <T>Tercihiniz hesabınıza kaydedilir; başka cihazdan giriş yaptığınızda da korunur.</T>
          </p>
        </div>
      </section>
    </main>
  );
}
